import { yupResolver } from "@hookform/resolvers/yup";
import useIsMounted from "ismounted";
import { merge } from "lodash";
import { FunctionComponent, useContext, useEffect, useMemo } from "react";
import { DeepPartial, FormProvider, useForm } from "react-hook-form";
import { Observable, Subject } from "rxjs";
import { object, reach } from "yup";
import { StepperContext } from "../../../../../../../../components/stepper/context";
import { VerificationDTO } from "../../../../../../../../store/verification/models/dto/due-diligence";
import { ConfigContext } from "../../../config-context";
import { Address } from "../address";

type KycRequest = VerificationDTO.Requests.DueDiligence.Form.Post.Final.T["kycRequests"][number];

const AddressStep: FunctionComponent<{
  setData: (d: DeepPartial<KycRequest>) => void;
  data: DeepPartial<KycRequest>;
}> = ({ data, setData }) => {
  const {
    state: { dueDiligenceId },
  } = useContext(ConfigContext);
  const {
    singleKycRequest,
  } = VerificationDTO.Requests.DueDiligence.Form.Post.Final.useValidations(
    dueDiligenceId
  );
  const { setValidation } = useContext(StepperContext);
  const isMounted = useIsMounted();
  const newValidation = useMemo(
    () =>
      object().shape({
        userDetails: reach(singleKycRequest, "userDetails").pick(["address"]),
      }),
    [singleKycRequest]
  );

  const form = useForm<{
    userDetails: Pick<KycRequest["userDetails"], "address">;
  }>({
    resolver: yupResolver(newValidation),
    defaultValues: useMemo(() => {
      const result = {};
      merge(
        result,
        {
          userDetails: {
            locale: "en",
          },
          socialMediaLinks: [],
        },
        data
      );
      return result as typeof data;
    }, [data]),
  });

  const { handleSubmit } = form;

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        handleSubmit((d) => {
          setData(d);
          res();
        })();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [setData, setValidation, handleSubmit, isMounted.current]);

  return (
    <FormProvider {...form}>
      <Address path={`userDetails.address`} errorPath={`userDetails.address`} />
    </FormProvider>
  );
};

export default AddressStep;
